import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import { ThemeProvider } from '@/context/ThemeContext';
import { AIFilterProvider } from '@/context/AIFilterContext';
import CookieConsent from '@/components/CookieConsent';
import PWAInstallPrompt from '@/components/PWAInstallPrompt';
import { useScreenProtection } from '@/hooks/useScreenProtection';
import axios from 'axios';
import '@/App.css';
import Landing from '@/pages/Landing';
import Login from '@/pages/Login';
import Register from '@/pages/Register';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import AuthCallback from '@/pages/AuthCallback';
import Onboarding from '@/pages/Onboarding';
import Dashboard from '@/pages/Dashboard';
import Feed from '@/pages/Feed';
import Explore from '@/pages/Explore';
import Videos from '@/pages/Videos';
import VideoUpload from '@/pages/VideoUpload';
import Communities from '@/pages/Communities';
import Profile from '@/pages/Profile';
import Messages from '@/pages/Messages';
import Talkspace from '@/pages/Talkspace';
import LiveStream from '@/pages/LiveStream';
import Premium from '@/pages/Premium';
import Creator from '@/pages/Creator';
import Pricing from '@/pages/Pricing';
import SubscriptionSuccess from '@/pages/SubscriptionSuccess';
import Settings from '@/pages/Settings';
import AdminPanel from '@/pages/AdminPanel';
import Locale from '@/pages/Locale';
import Hub from '@/pages/Hub';
import SocyyCalendar from '@/pages/SocyyCalendar';
import AdvancedLocale from '@/pages/AdvancedLocale';
import Awards from '@/pages/Awards';
import HallOfFame from '@/pages/HallOfFame';
import SuggestionBox from '@/pages/SuggestionBox';
import VibeCheck from '@/pages/VibeCheck';
import DatingMingle from '@/pages/DatingMingle';
import HubPost from '@/pages/HubPost';
import CreatorDashboard from '@/pages/CreatorDashboard';
import LocalePayments from '@/pages/LocalePayments';
import AIAssistant from '@/pages/AIAssistant';
import Credits from '@/pages/Credits';
import AgencyPortal from '@/pages/AgencyPortal';
import CreativityZone from '@/pages/CreativityZone';
import TimeCapsule from '@/pages/TimeCapsule';
import DigitalWill from '@/pages/DigitalWill';
import SocyyShop from '@/pages/SocyyShop';
import GameArena from '@/pages/GameArena';
import ThreadPage from '@/pages/ThreadPage';
import SecurityPrivacy from '@/pages/SecurityPrivacy';
import PrivacyPolicy from '@/pages/legal/PrivacyPolicy';
import TermsOfService from '@/pages/legal/TermsOfService';
import CodeOfConduct from '@/pages/legal/CodeOfConduct';
import IPProtection from '@/pages/legal/IPProtection';
import AdminDashboard from '@/pages/AdminDashboard';
import PrivacySettings from '@/pages/PrivacySettings';
import LivenessVerification from '@/pages/LivenessVerification';
import PublicProfile from '@/pages/PublicProfile';
import ProtectedRoute from '@/components/ProtectedRoute';

// Global axios interceptor - always send auth token if available
axios.interceptors.request.use((config) => {
  config.withCredentials = true;
  if (!config.headers.Authorization) {
    const token = localStorage.getItem('session_token') || 
                  localStorage.getItem('token') || 
                  localStorage.getItem('socyy_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

function AppRouter() {
  const location = useLocation();
  
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/conduct" element={<CodeOfConduct />} />
      <Route path="/ip-protection" element={<IPProtection />} />
      <Route path="/u/:username" element={<PublicProfile />} />
      
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
      <Route path="/feed" element={<ProtectedRoute><Feed /></ProtectedRoute>} />
      <Route path="/explore" element={<ProtectedRoute><Explore /></ProtectedRoute>} />
      <Route path="/videos" element={<ProtectedRoute><Videos /></ProtectedRoute>} />
      <Route path="/upload-video" element={<ProtectedRoute><VideoUpload /></ProtectedRoute>} />
      <Route path="/communities" element={<ProtectedRoute><Communities /></ProtectedRoute>} />
      <Route path="/locale" element={<ProtectedRoute><Locale /></ProtectedRoute>} />
      <Route path="/locale/advanced" element={<ProtectedRoute><AdvancedLocale /></ProtectedRoute>} />
      <Route path="/locale/payments" element={<ProtectedRoute><LocalePayments /></ProtectedRoute>} />
      <Route path="/hub" element={<ProtectedRoute><Hub /></ProtectedRoute>} />
      <Route path="/calendar" element={<ProtectedRoute><SocyyCalendar /></ProtectedRoute>} />
      <Route path="/awards" element={<ProtectedRoute><Awards /></ProtectedRoute>} />
      <Route path="/suggestions" element={<ProtectedRoute><SuggestionBox /></ProtectedRoute>} />
      <Route path="/hall-of-fame" element={<ProtectedRoute><HallOfFame /></ProtectedRoute>} />
      <Route path="/dating" element={<ProtectedRoute><DatingMingle /></ProtectedRoute>} />
      <Route path="/hubpost" element={<ProtectedRoute><HubPost /></ProtectedRoute>} />
      <Route path="/creator" element={<ProtectedRoute><CreatorDashboard /></ProtectedRoute>} />
      <Route path="/agency" element={<ProtectedRoute><AgencyPortal /></ProtectedRoute>} />
      <Route path="/ai-assistant" element={<ProtectedRoute><AIAssistant /></ProtectedRoute>} />
      <Route path="/credits" element={<ProtectedRoute><Credits /></ProtectedRoute>} />
      <Route path="/credits/success" element={<ProtectedRoute><Credits /></ProtectedRoute>} />
      <Route path="/creativity-zone" element={<ProtectedRoute><CreativityZone /></ProtectedRoute>} />
      <Route path="/timecapsule" element={<ProtectedRoute><TimeCapsule /></ProtectedRoute>} />
      <Route path="/shop" element={<ProtectedRoute><SocyyShop /></ProtectedRoute>} />
      <Route path="/arena" element={<ProtectedRoute><GameArena /></ProtectedRoute>} />
      <Route path="/digital-will" element={<ProtectedRoute><DigitalWill /></ProtectedRoute>} />
      <Route path="/thread/:threadId" element={<ProtectedRoute><ThreadPage /></ProtectedRoute>} />
      <Route path="/security" element={<SecurityPrivacy />} />
      <Route path="/privacy" element={<SecurityPrivacy />} />
      <Route path="/locale/:localeId/vibe" element={<ProtectedRoute><VibeCheck /></ProtectedRoute>} />
      <Route path="/profile/:userId" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
      <Route path="/talkspace" element={<ProtectedRoute><Talkspace /></ProtectedRoute>} />
      <Route path="/talkspace/:roomId" element={<ProtectedRoute><Talkspace /></ProtectedRoute>} />
      <Route path="/live" element={<ProtectedRoute><LiveStream /></ProtectedRoute>} />
      <Route path="/live/:streamId" element={<ProtectedRoute><LiveStream /></ProtectedRoute>} />
      <Route path="/premium" element={<ProtectedRoute><Premium /></ProtectedRoute>} />
      <Route path="/subscription/success" element={<ProtectedRoute><SubscriptionSuccess /></ProtectedRoute>} />
      <Route path="/creator" element={<ProtectedRoute><Creator /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminPanel /></ProtectedRoute>} />
      <Route path="/admin/dashboard" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
      <Route path="/privacy-settings" element={<ProtectedRoute><PrivacySettings /></ProtectedRoute>} />
      <Route path="/verify/liveness" element={<ProtectedRoute><LivenessVerification /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  // Enable screen capture prevention
  useScreenProtection();

  // Register service worker for PWA
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      window.addEventListener('load', () => {
        navigator.serviceWorker.register('/sw.js')
          .then((registration) => {
            console.log('ServiceWorker registration successful:', registration.scope);
          })
          .catch((error) => {
            console.log('ServiceWorker registration failed:', error);
          });
      });
    }
  }, []);

  return (
    <ThemeProvider>
      <AIFilterProvider>
        <div className="App">
          <BrowserRouter>
            <AppRouter />
            <Toaster position="top-right" />
            <CookieConsent />
            <PWAInstallPrompt />
          </BrowserRouter>
        </div>
      </AIFilterProvider>
    </ThemeProvider>
  );
}

export default App;